<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-50 px-4">
    <div class="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
      <!-- Title -->
      <div class="text-center mb-6">
        <h2 class="text-2xl font-semibold text-gray-800">
          {{ schoolName || "" }}
        </h2>
        <p class="text-sm text-gray-500 mt-1">
          Create your account below to accept the invitation and get started.
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="space-y-5">
        <!-- Full Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Enter your full name"
            class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
          />
          <small v-if="fieldError('name')" class="text-red-600">{{ fieldError('name') }}</small>
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            v-model="form.email"
            type="email"
            disabled
            class="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-100 cursor-not-allowed focus:outline-none"
          />
          <small v-if="fieldError('email') && !hasExistingAccount" class="text-red-600">{{ fieldError('email') }}</small>
        </div>

        <!-- The invited email is already registered: accept with that account -->
        <div
          v-if="hasExistingAccount"
          data-testid="invitation-existing-account"
          class="rounded-lg border border-primary/20 bg-primary/10 p-4 text-sm text-gray-700 space-y-3"
        >
          <p>
            <strong>This email already has a Gradeverse account.</strong>
            You don't need a new one — enter that account's password to accept the invitation.
          </p>
          <div>
            <label for="invitation-existing-password" class="block text-sm font-medium text-gray-700 mb-1">
              Your Gradeverse password
            </label>
            <Password
              v-model="existingPassword"
              input-id="invitation-existing-password"
              toggle-mask
              :feedback="false"
              :invalid="!!existingPasswordError"
              fluid
              placeholder="Password for this email"
              data-testid="invitation-existing-password"
              @keydown.enter.prevent="acceptWithExistingAccount"
            />
            <small v-if="existingPasswordError" class="text-red-600" data-testid="invitation-existing-password-error">
              {{ existingPasswordError }}
            </small>
            <small v-else class="text-gray-500">
              Forgotten it?
              <RouterLink to="/forgot-password" class="text-primary font-medium hover:underline">Reset your password</RouterLink>,
              then come back to this link.
            </small>
          </div>
          <Button
            type="button"
            label="Accept with my existing account"
            icon="pi pi-check"
            size="small"
            class="w-full"
            :loading="isAccepting"
            data-testid="invitation-accept-existing"
            @click="acceptWithExistingAccount"
          />
        </div>

        <!-- Password -->
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="form.password"
            placeholder="Enter your password"
            class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
          />
          <button
            type="button"
            @click="togglePassword"
            class="absolute right-3 top-8 text-gray-500 hover:text-gray-700"
          >
            <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
          </button>
        </div>
        <small v-if="fieldError('password')" class="text-red-600">{{ fieldError('password') }}</small>
        <small v-else class="text-gray-500">At least 8 characters.</small>

        <!-- Confirm Password -->
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
          <input
            :type="showConfirm ? 'text' : 'password'"
            v-model="form.password_confirmation"
            placeholder="Re-enter your password"
            class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
          />
          <button
            type="button"
            @click="toggleConfirm"
            class="absolute right-3 top-8 text-gray-500 hover:text-gray-700"
          >
            <i :class="showConfirm ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
          </button>
          <small v-if="fieldError('password_confirmation')" class="text-red-600">{{ fieldError('password_confirmation') }}</small>
        </div>

        <!-- Button -->
        <button
          type="submit"
          class="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary transition"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Creating account...' : 'Accept and Create Account' }}
        </button>

        <!-- Login Link -->
        <div v-if="!hasExistingAccount" class="text-center text-sm text-gray-600 mt-3">
          Already have a Gradeverse account?
          <button
            type="button"
            class="text-primary font-medium hover:underline"
            data-testid="invitation-accept-existing-link"
            @click="showExistingAccount"
          >
            Accept with your existing account
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAcceptInvitationForm } from '@/composables/useAcceptInvitationForm';

const route = useRoute();
const token = (route.params.token as string) || '';

const {
  form,
  isSubmitting,
  isAccepting,
  hasExistingAccount,
  existingPassword,
  existingPasswordError,
  fieldError,
  createAccount,
  showExistingAccount,
  acceptWithExistingAccount,
} = useAcceptInvitationForm(token);

const schoolName = ref<string | null>(null);
const showPassword = ref(false);
const showConfirm = ref(false);

// School name and invited email come from the invitation link.
onMounted(() => {
  const query = route.query;
  if (query.school) schoolName.value = decodeURIComponent(query.school as string);
  if (query.email) form.value.email = decodeURIComponent(query.email as string);
});

const togglePassword = () => (showPassword.value = !showPassword.value);
const toggleConfirm = () => (showConfirm.value = !showConfirm.value);

const handleSubmit = () => createAccount();
</script>
